import React from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Search, ShoppingBag, Plus, Minus } from 'lucide-react';

const ConsumablesTab = ({
  consumables,
  allRecommendations,
  searchQuery,
  setSearchQuery,
  consumablesCart,
  addToConsumablesCart,
  updateConsumableQuantity,
  addToCart,
  language
}) => {
  const filteredConsumables = consumables.filter(item => 
    item.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    item.brand?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="space-y-4">
      {/* Consumables recommendations */}
      {allRecommendations.consumables?.length > 0 && (
        <Card className="bg-gradient-to-r from-orange-50 to-amber-50 border-orange-200">
          <CardContent className="p-4">
            <h3 className="font-semibold text-orange-800 mb-3 flex items-center gap-2">
              <span className="text-lg">⭐</span>
              {language === 'fr' ? 'Consommables recommandés' : 'Recommended consumables'}
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {allRecommendations.consumables.slice(0, 4).map(product => (
                <Card key={product.id} className="bg-white border-orange-100 hover:shadow-md transition-shadow">
                  <CardContent className="p-3">
                    <div className="text-center">
                      <div className="w-12 h-12 mx-auto mb-2 bg-orange-50 rounded-full flex items-center justify-center">
                        <ShoppingBag className="w-6 h-6 text-orange-600" />
                      </div>
                      <p className="text-xs font-medium text-[#5D4E37] line-clamp-2 mb-1">{product.name}</p>
                      <p className="text-sm font-bold text-orange-600">{product.price?.toLocaleString()} F</p>
                      <Button 
                        size="sm" 
                        className="mt-2 w-full bg-orange-600 hover:bg-orange-700 text-white text-xs h-7"
                        onClick={() => addToCart(product)}
                      >
                        <Plus className="w-3 h-3 mr-1" />
                        {language === 'fr' ? 'Ajouter' : 'Add'}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Recommendation banner for consumables */}
      <Card className="border-orange-200 bg-orange-50">
        <CardContent className="p-4">
          <div className="flex items-center gap-2 mb-2">
            <ShoppingBag className="w-5 h-5 text-orange-600" />
            <h3 className="font-semibold text-orange-800">
              {language === 'fr' ? 'Essentiels pour le nettoyage' : 'Cleaning essentials'}
            </h3>
          </div>
          <p className="text-sm text-orange-700 mb-3">
            {language === 'fr' 
              ? 'Cotons, compresses et accessoires pour une routine de soin complète.'
              : 'Cotton pads, compresses and accessories for a complete skincare routine.'
            }
          </p>
        </CardContent>
      </Card>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#78716C]" />
        <Input
          placeholder={language === 'fr' ? 'Rechercher un consommable...' : 'Search consumables...'}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-12 input-default"
          data-testid="consumable-search"
        />
      </div>

      {/* Consumables Grid */}
      {filteredConsumables.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center">
            <ShoppingBag className="w-12 h-12 mx-auto mb-4 text-[#78716C] opacity-50" />
            <p className="text-[#78716C]">
              {language === 'fr' ? 'Aucun consommable disponible' : 'No consumables available'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredConsumables.map((item) => {
            const inCart = consumablesCart.find(c => c.item_id === item.id);
            return (
              <Card 
                key={item.id}
                className="relative overflow-hidden hover:shadow-md transition-shadow"
              >
                <CardContent className="p-4">
                  <div className="flex gap-4">
                    <div className="w-16 h-16 bg-orange-50 rounded-xl flex items-center justify-center flex-shrink-0">
                      {item.image_url ? (
                        <img 
                          src={item.image_url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${item.image_url}` : item.image_url}
                          alt={item.name}
                          className="w-full h-full object-cover rounded-xl"
                        />
                      ) : (
                        <ShoppingBag className="w-8 h-8 text-orange-400" />
                      )}
                    </div>

                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium text-[#0F172A] text-sm truncate">{item.name}</h3>
                      {item.brand && (
                        <p className="text-xs text-[#78716C]">{item.brand}</p>
                      )}
                      {item.subcategory && (
                        <Badge variant="outline" className="text-xs mt-1 capitalize">
                          {item.subcategory}
                        </Badge>
                      )}
                      <div className="flex items-center justify-between mt-2">
                        <span className="text-orange-600 font-semibold">
                          {item.price?.toLocaleString('fr-FR')} FCFA
                        </span>
                        {inCart ? (
                          <div className="flex items-center gap-2">
                            <Button
                              size="icon"
                              variant="outline"
                              className="h-7 w-7"
                              onClick={() => updateConsumableQuantity(item.id, -1)}
                            >
                              <Minus className="w-3 h-3" />
                            </Button>
                            <span className="w-6 text-center font-medium">{inCart.quantity}</span>
                            <Button
                              size="icon"
                              variant="outline"
                              className="h-7 w-7"
                              onClick={() => updateConsumableQuantity(item.id, 1)}
                            >
                              <Plus className="w-3 h-3" />
                            </Button>
                          </div>
                        ) : (
                          <Button
                            size="sm"
                            className="bg-orange-500 hover:bg-orange-600 text-white"
                            onClick={() => addToConsumablesCart(item)}
                          >
                            <Plus className="w-4 h-4 mr-1" />
                            {language === 'fr' ? 'Ajouter' : 'Add'}
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ConsumablesTab;
